// Pure helpers of the «Система → Бэкапы» card (src/components/BackupsCard.tsx): sizes and
// ages the Russian way, and the words + tones of each state (rule 21: colour + icon + word).

import { TIME_UNIT } from './format'

export type BackupState = 'healthy' | 'stale' | 'failed' | 'verify_failed' | 'empty'
export type Tone = 'ok' | 'warn' | 'danger' | 'neutral'

const UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ']

/** 258051 → «252 КБ», 81398205 → «77,6 МБ». Binary steps, one decimal below 100. */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—'
  let v = bytes
  let i = 0
  while (v >= 1024 && i < UNITS.length - 1) {
    v /= 1024
    i += 1
  }
  const digits = i === 0 || v >= 100 ? 0 : 1
  const num = v.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits })
  return `${num} ${UNITS[i]}`
}

/** Seconds → «45 с», «12 мин», «2 ч 15 мин», «3 д 4 ч». */
export function fmtAge(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec) || sec < 0) return '—'
  const { second: S, minute: M, hour: H, day: D } = TIME_UNIT
  const s = Math.floor(sec)
  if (s < 60) return `${s} ${S}`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} ${M}`
  const h = Math.floor(m / 60)
  if (h < 48) return m % 60 ? `${h} ${H} ${m % 60} ${M}` : `${h} ${H}`
  const d = Math.floor(h / 24)
  return h % 24 ? `${d} ${D} ${h % 24} ${H}` : `${d} ${D}`
}

/** Interval in minutes → «6 ч», «90 мин», «1 д». */
export function fmtInterval(min: number): string {
  return fmtAge(min * 60)
}

export const STATE_VIEW: Record<BackupState, { tone: Tone; label: string; hint: string }> = {
  healthy: {
    tone: 'ok',
    label: 'В порядке',
    hint: 'Свежий бэкап есть, последняя проверка восстановлением прошла.',
  },
  failed: {
    tone: 'warn',
    label: 'Последний запуск не удался',
    hint:
      'Свежий бэкап есть, но последняя попытка упала — причина в таблице ниже. ' +
      'Если следующая тоже не пройдёт, придёт алерт.',
  },
  verify_failed: {
    tone: 'danger',
    label: 'Проверка не прошла',
    hint:
      'Последний бэкап не восстановился во временную базу или данные не совпали. ' +
      'Запустите make backup-verify и посмотрите причину ниже.',
  },
  stale: {
    tone: 'danger',
    label: 'Бэкап устарел',
    hint:
      'Свежего бэкапа нет дольше двух интервалов. Проверьте контейнер: ' +
      'docker compose ps db-backup и docker compose logs db-backup.',
  },
  empty: {
    tone: 'danger',
    label: 'Бэкапов нет',
    hint:
      'Ни одного запуска не записано. Запустите контейнер: docker compose up -d db-backup, ' +
      'или разово: make backup.',
  },
}

export function runKindLabel(kind: string): string {
  return kind === 'verify' ? 'Проверка' : 'Бэкап'
}
